<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatTime } from '../../constants';

// Fila de indicadores del mockup: cinco cifras grandes con su etiqueta en
// mayúsculas y una línea de contexto debajo. Todas salen de la misma
// respuesta que alimenta el embudo, así que respetan los filtros de la
// tarjeta principal.
const props = defineProps({
  cohortSummary: { type: Object, default: () => ({}) },
  avgFirstResponseTime: { type: Number, default: 0 },
  neverResponded: { type: Number, default: 0 },
  attention: { type: Object, default: () => ({}) },
});

const { t, locale } = useI18n();
const key = 'OVERVIEW_REPORTS.KPI';

const numberFormatter = computed(
  () => new Intl.NumberFormat(locale.value || 'es')
);
const moneyFormatter = computed(
  () =>
    new Intl.NumberFormat(locale.value || 'es', { maximumFractionDigits: 0 })
);

const formatNumber = value => numberFormatter.value.format(Number(value) || 0);

const cards = computed(() => {
  const summary = props.cohortSummary || {};
  const attention = props.attention || {};

  const list = [
    {
      key: 'leads',
      label: t(`${key}.LEADS`),
      value: formatNumber(summary.total_contacts),
      hint: t(`${key}.LEADS_HINT`),
    },
    {
      key: 'won',
      label: t(`${key}.WON`),
      value: formatNumber(summary.total_won),
      hint: summary.has_deal_amount
        ? t(`${key}.WON_HINT_REVENUE`, {
            amount: moneyFormatter.value.format(
              Number(summary.total_revenue) || 0
            ),
          })
        : t(`${key}.WON_HINT`),
    },
    {
      key: 'conversion',
      label: t(`${key}.CONVERSION`),
      value: `${summary.conversion_rate ?? 0}%`,
      hint: t(`${key}.CONVERSION_HINT`, {
        count: formatNumber(summary.total_contacts),
      }),
    },
    {
      key: 'frt',
      label: t(`${key}.FIRST_RESPONSE`),
      value: props.avgFirstResponseTime
        ? formatTime(props.avgFirstResponseTime)
        : '—',
      hint: t(`${key}.FIRST_RESPONSE_HINT`),
    },
    {
      key: 'never_responded',
      label: t(`${key}.NEVER_RESPONDED`),
      value: formatNumber(props.neverResponded),
      hint: attention.total
        ? t(`${key}.NEVER_RESPONDED_HINT`, {
            count: formatNumber(attention.total),
          })
        : t(`${key}.NEVER_RESPONDED_HINT_EMPTY`),
      alert: props.neverResponded > 0,
    },
  ];
  return list;
});
</script>

<template>
  <div
    class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
  >
    <div
      v-for="card in cards"
      :key="card.key"
      class="flex flex-col gap-1 px-4 py-3.5 border rounded-xl border-n-weak bg-n-solid-2"
    >
      <span
        class="text-[10.5px] font-semibold tracking-[0.09em] uppercase text-n-slate-10"
      >
        {{ card.label }}
      </span>
      <span
        class="text-2xl font-bold tracking-tight tabular-nums"
        :class="card.alert ? 'text-n-ruby-11' : 'text-n-slate-12'"
      >
        {{ card.value }}
      </span>
      <span class="text-xs text-n-slate-10">{{ card.hint }}</span>
    </div>
  </div>
</template>
